import React, { useState, useEffect, useContext, useCallback } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import VisibilityIcon from '@mui/icons-material/Visibility';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useNavigate } from 'react-router-dom';
import axios from 'src/axios';
import { APICONSTANTS, CONSTANTS } from 'src/components/Constants';
import { AppContext, SnackContext } from 'src/store/ContextStore';
import { EVENTS } from 'src/components/admin/appReducer';
import usePersonLinkStatus from 'src/hooks/usePersonLinkStatus';
import { TILE_COLOURS } from './tileColours';
import PreviewDialogBox from '../../../smart/PreviewDialogBox';
import CustomDialog from 'src/components/UI/dialog/CustomDialog';
import { useAutoTranslation } from 'src/hooks/useTranslate';

function PersonRecordTile({
  name,
  familyMemberId,
  onView,
  onOpen,
  openLoading,
  onUnlink,
  unlinkLoading,
  unlinkDisabled
}) {
  const t = useAutoTranslation();

  const displayName = name || familyMemberId || '—';
  const showId = name && familyMemberId;

  const c = TILE_COLOURS.phenotype;

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        backgroundColor: c.background,
        border: `1px solid ${c.border}`,
        borderLeft: `3px solid ${c.accent}`,
        borderRadius: '6px',
        px: '10px',
        py: '8px',
        mb: 1
      }}
    >
      <Box sx={{ flex: 1, minWidth: 0, mr: 1 }}>
        <Typography
          variant="subtitle1"
          noWrap
          sx={{ fontWeight: 600, color: c.text, lineHeight: 1.3 }}
        >
          {displayName}
        </Typography>
        {showId && (
          <Typography
            variant="caption"
            noWrap
            sx={{ color: c.subtext, lineHeight: 1.3 }}
          >
            {familyMemberId}
          </Typography>
        )}
      </Box>

      <Box sx={{ display: 'flex', gap: 0.25, flexShrink: 0 }}>
        {onView && (
          <Tooltip title={t('View details')}>
            <IconButton size="small" onClick={onView}>
              <VisibilityIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        {onOpen && (
          <Tooltip title={t('Go to record')}>
            <span>
              <IconButton
                size="small"
                onClick={onOpen}
                disabled={openLoading || !familyMemberId}
              >
                {openLoading ? (
                  <CircularProgress size={14} />
                ) : (
                  <OpenInNewIcon fontSize="small" />
                )}
              </IconButton>
            </span>
          </Tooltip>
        )}
        {onUnlink && (
          <Tooltip title={t('Remove link')}>
            <span>
              <IconButton
                size="small"
                onClick={onUnlink}
                disabled={unlinkDisabled}
              >
                {unlinkLoading ? (
                  <CircularProgress size={14} />
                ) : (
                  <LinkOffIcon fontSize="small" />
                )}
              </IconButton>
            </span>
          </Tooltip>
        )}
      </Box>
    </Box>
  );
}

function SearchSecondaryDialog({ open, onClose, currentPerson, onLinked }) {
  const t = useAutoTranslation();

  const { setSnack } = useContext(SnackContext);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [linking, setLinking] = useState(null);
  const familyMemberId = currentPerson?.familyMemberId;

  const doSearch = useCallback(async () => {
    if (!familyMemberId) return;
    setSearching(true);
    try {
      const url =
        APICONSTANTS.searchPersonLinks +
        `?familyMemberId=${encodeURIComponent(familyMemberId)}` +
        (query ? `&name=${encodeURIComponent(query)}` : '');
      const { data } = await axios.get(url);
      setResults(data);
    } catch {
      setSnack({ status: true, msg: t('Search failed'), severity: 'error' });
    } finally {
      setSearching(false);
    }
  }, [familyMemberId, query, setSnack]);

  useEffect(() => {
    if (!open) {
      setQuery('');
      setResults([]);
      return;
    }
    doSearch();
  }, [open, doSearch]);

  const handleSearch = () => doSearch();

  const handleSelect = async (secondary) => {
    setLinking(secondary.familyMemberId);
    try {
      await axios.post(APICONSTANTS.createPersonLink, {
        primaryFamilyMemberId: familyMemberId,
        secondaryFamilyMemberId: secondary.familyMemberId
      });
      setSnack({
        status: true,
        msg: t('{{familyMemberId}} linked as secondary record', {
          familyMemberId: secondary.familyMemberId
        }),
        severity: 'success'
      });
      onLinked();
      onClose();
    } catch (err) {
      const msg = err?.response?.data?.message || t('Failed to create link');
      setSnack({ status: true, msg, severity: 'error' });
    } finally {
      setLinking(null);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ fontWeight: 700, py: 2 }}>
        {t('Search for Secondary Person')}
      </DialogTitle>
      {/* TGCLOUD-1711: pt restores the gap under the painted title bar (MUI
          zeroes it via `.MuiDialogTitle-root + &`, which outranks a plain sx
          class). The title keeps its own fontWeight/py — this is a search
          dialog, not a confirmation, so the house `pb: 1` does not apply. */}
      <DialogContent
        sx={{
          pt: '16px !important',
          overflowX: 'hidden',
          '&::-webkit-scrollbar': { width: 0 },
          scrollbarWidth: 'none'
        }}
      >
        {currentPerson && (
          <Paper
            variant="outlined"
            sx={{
              p: 2,
              borderRadius: 2,
              my: 2,
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 1.5
            }}
          >
            <Box>
              <Typography
                variant="caption"
                sx={{
                  color: 'text.secondary'
                }}
              >
                {t('G-Number')}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 500
                }}
              >
                {currentPerson.familyMemberId || '—'}
              </Typography>
            </Box>
            <Box>
              <Typography
                variant="caption"
                sx={{
                  color: 'text.secondary'
                }}
              >
                {t('Date of Birth')}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 500
                }}
              >
                {currentPerson.dob || '—'}
              </Typography>
            </Box>
            <Box>
              <Typography
                variant="caption"
                sx={{
                  color: 'text.secondary'
                }}
              >
                {t('Family Name')}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 500
                }}
              >
                {currentPerson.lastName || '—'}
              </Typography>
            </Box>
            <Box>
              <Typography
                variant="caption"
                sx={{
                  color: 'text.secondary'
                }}
              >
                {t('Given Name')}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 500
                }}
              >
                {currentPerson.firstName || '—'}
              </Typography>
            </Box>
          </Paper>
        )}

        <TextField
          fullWidth
          size="small"
          placeholder={t('Search by name…')}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
          sx={{ mb: 1.5 }}
          slotProps={{
            input: {
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={handleSearch}
                    disabled={searching}
                  >
                    <SearchIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              )
            }
          }}
        />

        {searching ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              py: 3
            }}
          >
            <CircularProgress size={24} />
          </Box>
        ) : results.length === 0 ? (
          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary',
              py: 1
            }}
          >
            {t('No matching candidates found.')}
          </Typography>
        ) : (
          <TableContainer
            component={Paper}
            variant="outlined"
            sx={{ overflowX: 'hidden' }}
          >
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.50' }}>
                  <TableCell>
                    <b>{t('G-Number')}</b>
                  </TableCell>
                  <TableCell>
                    <b>{t('Family Name')}</b>
                  </TableCell>
                  <TableCell>
                    <b>{t('Given Name')}</b>
                  </TableCell>
                  <TableCell>
                    <b>{t('DOB')}</b>
                  </TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {results.map((r) => (
                  <TableRow key={r.familyMemberId} hover>
                    <TableCell>{r.familyMemberId}</TableCell>
                    <TableCell>{r.lastName || '—'}</TableCell>
                    <TableCell>{r.firstName || '—'}</TableCell>
                    <TableCell>{r.dob || '—'}</TableCell>
                    <TableCell align="right">
                      <Button
                        size="small"
                        variant="contained"
                        disabled={!!linking}
                        onClick={() => handleSelect(r)}
                        sx={{ whiteSpace: 'nowrap' }}
                      >
                        {linking === r.familyMemberId ? (
                          <CircularProgress
                            size={14}
                            sx={{ color: 'inherit' }}
                          />
                        ) : (
                          t('Select')
                        )}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={!!linking}>
          {t('Close')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default function RecordStatusMenu({
  data,
  refreshKey,
  onLinkStatusChanged
}) {
  const t = useAutoTranslation();

  const { setSnack } = useContext(SnackContext);
  const { dispatchUpdate } = useContext(AppContext);
  const navigate = useNavigate();
  const [navigating, setNavigating] = useState(null);

  const familyMemberId = data?.proBandId ?? data?.familyMemberId ?? null;

  const currentPerson = familyMemberId
    ? {
        familyMemberId,
        firstName: data?.firstName ?? null,
        lastName: data?.lastName ?? null,
        dob: data?.dob ?? null
      }
    : null;

  const {
    status,
    linkData,
    loading,
    refetch: fetchStatus
  } = usePersonLinkStatus(familyMemberId);

  // Refetch when data object reference changes (e.g. navigating back to the same
  // person after visiting another pedigree — familyMemberId value is unchanged so
  // the hook's own useEffect won't fire again).
  useEffect(() => {
    if (familyMemberId) fetchStatus();
  }, [data]); // eslint-disable-line react-hooks/exhaustive-deps

  // Refetch when the parent signals a save completed (e.g. after primary data is
  // saved and synced to secondaries, so tile names stay current).
  useEffect(() => {
    if (refreshKey) fetchStatus();
  }, [refreshKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const [removing, setRemoving] = useState(null);
  const [confirmUnlinkId, setConfirmUnlinkId] = useState(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const [previewFamilyId, setPreviewFamilyId] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');

  const handlePreview = (familyMemberId) => {
    if (!familyMemberId) return;
    const familyId = familyMemberId.split('-').slice(0, 3).join('-');
    setPreviewFamilyId(familyId);
    setPreviewUrl(
      `/admin/pedigree/${familyId}?embed=chart&highlight=${familyMemberId}`
    );
  };

  const handleOpenPerson = async (gNum, person) => {
    if (!gNum) return;
    setNavigating(gNum);
    try {
      let resolved = person;
      if (!resolved?.mongoId || !resolved?.familyId) {
        const { data: r } = await axios.get(
          APICONSTANTS.resolvePersonLink.replace(
            ':familyMemberId',
            encodeURIComponent(gNum)
          )
        );
        resolved = r;
      }
      const { mongoId, familyId } = resolved;
      const { data: family } = await axios.get(
        APICONSTANTS.getFamiliesInfo.replace(':user_id', mongoId)
      );
      dispatchUpdate({
        type: EVENTS.SET_PEDIGREE_SELECTED_USER,
        value: { family, selectedUser: { ...resolved, _id: mongoId } }
      });
      navigate({
        pathname: CONSTANTS.pedigreeChartPath.replace(':_id', familyId),
        state: { selectedFamilyMemberId: gNum }
      });
    } catch {
      setSnack({
        status: true,
        msg: t('Could not open pedigree'),
        severity: 'error'
      });
    } finally {
      setNavigating(null);
    }
  };

  const handleRemoveLink = async (linkId) => {
    setRemoving(linkId);
    try {
      await axios.delete(APICONSTANTS.deletePersonLink.replace(':id', linkId));
      setSnack({
        status: true,
        msg: t('Record link removed'),
        severity: 'success'
      });
      fetchStatus();
      onLinkStatusChanged?.();
    } catch {
      setSnack({
        status: true,
        msg: t('Failed to remove link'),
        severity: 'error'
      });
    } finally {
      setRemoving(null);
    }
  };

  if (!familyMemberId) {
    return (
      <Typography
        variant="body2"
        sx={{
          color: 'text.secondary'
        }}
      >
        {t('No person selected.')}
      </Typography>
    );
  }

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          py: 3
        }}
      >
        <CircularProgress size={24} />
      </Box>
    );
  }

  return (
    <Box>
      {status === 'secondary' && (
        <>
          <Typography variant="subtitle2" gutterBottom>
            {t('Primary Record')}
          </Typography>

          <PersonRecordTile
            name={
              [linkData?.primary?.firstName, linkData?.primary?.lastName]
                .filter(Boolean)
                .join(' ') || null
            }
            familyMemberId={
              linkData?.primaryFamilyMemberId ||
              linkData?.primary?.familyMemberId
            }
            onView={() =>
              handlePreview(
                linkData?.primaryFamilyMemberId ||
                  linkData?.primary?.familyMemberId
              )
            }
            onOpen={() => {
              const gNum =
                linkData?.primaryFamilyMemberId ??
                linkData?.primary?.familyMemberId;
              handleOpenPerson(gNum, linkData?.primary);
            }}
            openLoading={
              navigating ===
              (linkData?.primaryFamilyMemberId ??
                linkData?.primary?.familyMemberId)
            }
            onUnlink={() => setConfirmUnlinkId(linkData?.linkId)}
            unlinkLoading={removing === linkData?.linkId}
            unlinkDisabled={!!removing}
          />
        </>
      )}
      {status === 'primary' && (
        <>
          <Typography variant="subtitle2" gutterBottom>
            {t('Secondary Records')}
          </Typography>

          {!linkData?.secondaries?.length ? (
            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
                mb: 1.5
              }}
            >
              {t('No secondary records linked yet.')}
            </Typography>
          ) : (
            <Box sx={{ mb: 2 }}>
              {linkData.secondaries.map(({ linkId, person }) => (
                <PersonRecordTile
                  key={linkId}
                  name={
                    [person?.firstName, person?.lastName]
                      .filter(Boolean)
                      .join(' ') || null
                  }
                  familyMemberId={person?.familyMemberId}
                  onView={() => handlePreview(person?.familyMemberId)}
                  onOpen={() =>
                    handleOpenPerson(person?.familyMemberId, person)
                  }
                  openLoading={navigating === person?.familyMemberId}
                  onUnlink={() => setConfirmUnlinkId(linkId)}
                  unlinkLoading={removing === linkId}
                  unlinkDisabled={!!removing}
                />
              ))}
            </Box>
          )}

          <Button
            variant="outlined"
            size="small"
            onClick={() => setSearchOpen(true)}
          >
            {t('Search for Secondary Person')}
          </Button>
        </>
      )}
      {status === 'none' && (
        <>
          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary',
              mb: 2
            }}
          >
            {t(
              'This record has no primary or secondary link. You can designate another record with the same date of birth as a secondary (duplicate) of this one.'
            )}
          </Typography>
          <Button
            variant="outlined"
            size="small"
            onClick={() => setSearchOpen(true)}
          >
            {t('Search for Secondary Person')}
          </Button>
        </>
      )}
      <SearchSecondaryDialog
        open={searchOpen}
        onClose={() => setSearchOpen(false)}
        currentPerson={currentPerson}
        onLinked={() => {
          // Refresh both the sidebar (this person's status) AND the canvas +
          // legend (the batch map) — on creation, mirroring the unlink path.
          fetchStatus();
          onLinkStatusChanged?.();
        }}
      />
      <PreviewDialogBox
        open={!!previewFamilyId}
        onClose={() => {
          setPreviewFamilyId(null);
          setPreviewUrl('');
        }}
        familyId={previewFamilyId}
        previewUrl={previewUrl}
      />
      <CustomDialog
        open={!!confirmUnlinkId}
        handleClose={() => setConfirmUnlinkId(null)}
        onClose
        title="Remove Link"
        maxWidth="xs"
        minHeight="auto"
      >
        <Box>
          <Typography variant="body2">
            {t('Are you sure you want to remove this record link?')}
          </Typography>
          <Box
            sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 3 }}
          >
            <Button
              variant="outlined"
              size="small"
              onClick={() => setConfirmUnlinkId(null)}
              disabled={!!removing}
            >
              {t('Cancel')}
            </Button>
            <Button
              variant="contained"
              size="small"
              color="error"
              disabled={!!removing}
              startIcon={removing ? <CircularProgress size={14} /> : null}
              onClick={() => {
                const id = confirmUnlinkId;
                setConfirmUnlinkId(null);
                handleRemoveLink(id);
              }}
            >
              {t('Remove')}
            </Button>
          </Box>
        </Box>
      </CustomDialog>
    </Box>
  );
}
